import { useEffect, useMemo, useState } from "react";
import { useJson, nf, card, control, sectionLabel } from "./shared";
import { handOffBundle } from "../graph/handoff";

// STIX export for a profile: the entity plus whatever is related to it, filtered by
// object type, relationships and confidence, then copied, downloaded or opened in the Viewer.
// Identities and marking definitions that kept objects refer to (created_by_ref,
// object_marking_refs) always come along, so the bundle never points at nothing.

// Detection rules are indicators too, but a different thing to include or leave out.
const RULE_FACET = "x-detection-rule";
const facetOf = (o) => (o.type === "indicator" && o.pattern_type && o.pattern_type !== "stix" ? RULE_FACET : o.type);

const TYPE_GROUPS = [
  ["intrusion-set", "Threat groups"], ["campaign", "Campaigns"], ["malware", "Malware"], ["tool", "Tools"],
  ["attack-pattern", "Techniques"], ["course-of-action", "Mitigations"], ["vulnerability", "Vulnerabilities"],
  ["indicator", "Indicators"], [RULE_FACET, "Detection rules"], ["infrastructure", "Infrastructure"], ["ipv4-addr", "IPv4 addresses"], ["ipv6-addr", "IPv6 addresses"],
  ["domain-name", "Domains"], ["url", "URLs"], ["file", "Files"], ["identity", "Victims, sectors and publishers"], ["location", "Countries"],
];
const LABEL = Object.fromEntries(TYPE_GROUPS);
const CONF_OPTIONS = [[0, "Any confidence"], [1, "Low and up"], [30, "Medium and up"], [70, "High only"]];
const PREF_KEY = "ih-stix-export";
const PREVIEW_MAX = 150000;

const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREF_KEY) || "null"); } catch { return null; } };
const savePrefs = (p) => { try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* private mode */ } };
const newBundleId = (fallback) => { try { return `bundle--${crypto.randomUUID()}`; } catch { return fallback; } };

export function StixExport({ id, entity, ui, onOpenBundle }) {
  const { B, fs, HP } = ui;
  const { data, error, loading } = useJson(`/api/kb/entity/${encodeURIComponent(id)}/bundle`);
  const bundle = data?.bundle;
  const subjectId = entity.id;
  const isReport = entity.type === "report";

  // Types present besides the subject, and relationships.
  const facets = useMemo(() => {
    const counts = {};
    let rels = 0;
    const provenance = new Set((bundle?.objects || []).flatMap(o => [o.created_by_ref, ...(o.object_marking_refs || [])]).filter(Boolean));
    for (const o of bundle?.objects || []) {
      if (o.type === "relationship") { rels++; continue; }
      if (o.id === subjectId || provenance.has(o.id) || o.type === "marking-definition") continue;
      counts[facetOf(o)] = (counts[facetOf(o)] || 0) + 1;
    }
    return { counts, rels, provenance };
  }, [bundle, subjectId]);

  const prefs = useMemo(loadPrefs, []);
  const [excluded, setExcluded] = useState(() => new Set(prefs?.excluded || []));
  const [withRels, setWithRels] = useState(prefs?.withRels ?? true);
  const [minConf, setMinConf] = useState(prefs?.minConf ?? 0);
  const [copied, setCopied] = useState(false);
  useEffect(() => { savePrefs({ excluded: [...excluded], withRels, minConf }); }, [excluded, withRels, minConf]);

  const result = useMemo(() => {
    if (!bundle) return null;
    const byId = new Map(bundle.objects.map(o => [o.id, o]));
    const typeOn = (o) => o && (o.id === subjectId || !excluded.has(facetOf(o)));
    let keepObjs = new Set([subjectId]);
    let keepRels = [];
    if (withRels) {
      // Relationships whose ends are both allowed types and that meet the confidence bar,
      // then everything reachable from the subject through them.
      const rels = bundle.objects.filter(o => o.type === "relationship" && typeOn(byId.get(o.source_ref)) && typeOn(byId.get(o.target_ref)) && (!minConf || (o.confidence ?? 100) >= minConf));
      const adj = new Map();
      for (const r of rels) for (const [a, b] of [[r.source_ref, r.target_ref], [r.target_ref, r.source_ref]]) { if (!adj.has(a)) adj.set(a, []); adj.get(a).push(b); }
      // A report links to its contents through object_refs rather than relationships.
      const subject = byId.get(subjectId);
      for (const ref of [...(subject?.object_refs || []), ...(subject?.created_by_ref ? [subject.created_by_ref] : [])]) {
        const o = byId.get(ref);
        if (o && o.type !== "relationship" && typeOn(o)) { if (!adj.has(subjectId)) adj.set(subjectId, []); adj.get(subjectId).push(ref); }
      }
      const queue = [subjectId];
      while (queue.length) {
        const cur = queue.shift();
        for (const nb of adj.get(cur) || []) if (!keepObjs.has(nb)) { keepObjs.add(nb); queue.push(nb); }
      }
      keepRels = rels.filter(r => keepObjs.has(r.source_ref) && keepObjs.has(r.target_ref));
    } else {
      for (const o of bundle.objects) if (o.type !== "relationship" && typeOn(o) && !facets.provenance.has(o.id) && o.type !== "marking-definition") keepObjs.add(o.id);
    }
    // The identities and markings what's kept refers to.
    for (let grew = true; grew;) {
      grew = false;
      for (const o of bundle.objects) {
        if (!keepObjs.has(o.id) && !keepRels.includes(o)) continue;
        for (const ref of [o.created_by_ref, ...(o.object_marking_refs || [])]) if (ref && byId.has(ref) && !keepObjs.has(ref)) { keepObjs.add(ref); grew = true; }
      }
    }
    let objects = [...bundle.objects.filter(o => o.type !== "relationship" && keepObjs.has(o.id)), ...keepRels];
    // A report's object_refs list only what's kept (and at least one object, as STIX requires).
    objects = objects.map(o => {
      if (o.id !== subjectId || !Array.isArray(o.object_refs)) return o;
      const kept = new Set(objects.map(x => x.id));
      const refs = o.object_refs.filter(r => kept.has(r));
      return { ...o, object_refs: refs.length ? refs : (o.created_by_ref ? [o.created_by_ref] : o.object_refs.slice(0, 1)) };
    });
    const all = excluded.size === 0 && withRels && !minConf;
    return { type: "bundle", id: all ? bundle.id : newBundleId(bundle.id), objects };
  }, [bundle, excluded, withRels, minConf, subjectId, facets]);

  const json = useMemo(() => (result ? JSON.stringify(result, null, 2) : ""), [result]);
  const relCount = result ? result.objects.filter(o => o.type === "relationship").length : 0;
  const objCount = result ? result.objects.length - relCount : 0;
  const order = (t) => { const i = TYPE_GROUPS.findIndex(x => x[0] === t); return i < 0 ? 999 : i; };
  const types = Object.keys(facets.counts).sort((a, b) => order(a) - order(b) || a.localeCompare(b));

  const toggle = (t) => setExcluded(prev => { const n = new Set(prev); if (n.has(t)) n.delete(t); else n.add(t); return n; });
  const copy = () => navigator.clipboard?.writeText(json).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  const download = () => {
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const name = String(entity.name || entity.id).replace(/[^\w.-]+/g, "_").slice(0, 60);
    Object.assign(document.createElement("a"), { href: url, download: `${name}.stix.json` }).click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Building the bundle…</div>;
  if (error || !bundle) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error || "No bundle available."}</div>;

  const chip = (on) => ({ display: "inline-flex", alignItems: "center", gap: 6, fontSize: fs(12), color: on ? B.text : B.textDim, background: on ? B.primary + "18" : B.bgCard, border: `1px solid ${on ? B.primary + "55" : B.border}`, borderRadius: 20, padding: "5px 11px", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", textDecoration: on ? "none" : "line-through" });
  const btn = { ...HP.navBtn, padding: "7px 13px", fontSize: fs(12) };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card(ui), padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
          <span style={sectionLabel(ui)}>Include related objects</span>
          {types.length > 0 && (
            <span style={{ display: "flex", gap: 12, fontSize: fs(12) }}>
              <button onClick={() => setExcluded(new Set())} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", padding: 0, fontFamily: "'Satoshi', sans-serif" }}>All</button>
              <button onClick={() => setExcluded(new Set(types))} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", padding: 0, fontFamily: "'Satoshi', sans-serif" }}>None (just this object)</button>
            </span>
          )}
        </div>
        {types.length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {types.map(t => (
              <button key={t} onClick={() => toggle(t)} aria-pressed={!excluded.has(t)} style={chip(!excluded.has(t))}>
                <span aria-hidden="true">{excluded.has(t) ? "☐" : "☑"}</span>{LABEL[t] || t} <span style={{ color: B.textDim }}>{nf(facets.counts[t])}</span>
              </button>
            ))}
          </div>
        ) : <div style={{ fontSize: fs(12), color: B.textDim }}>Nothing else is linked to this object.</div>}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", fontSize: fs(12), color: B.textMuted }}>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={withRels} onChange={e => setWithRels(e.target.checked)} /> Include relationships <span style={{ color: B.textDim }}>({nf(facets.rels)})</span>
          </label>
          <select value={minConf} onChange={e => setMinConf(Number(e.target.value))} disabled={!withRels} aria-label="Minimum relationship confidence" style={{ ...control(ui), opacity: withRels ? 1 : 0.5 }}>
            {CONF_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span style={{ color: B.textDim }}>{withRels ? (isReport ? "Objects are kept if the report lists them or a kept relationship connects them." : "Objects are kept only if a kept relationship connects them to this one.") : "Objects without the relationships that connect them."}</span>
        </div>
      </div>

      <div style={{ ...card(ui), overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${B.border}`, flexWrap: "wrap" }}>
          <span style={{ flex: 1, fontSize: fs(12), color: B.textMuted }}><b style={{ color: B.text }}>{nf(objCount)}</b> object{objCount === 1 ? "" : "s"} · <b style={{ color: B.text }}>{nf(relCount)}</b> relationship{relCount === 1 ? "" : "s"} · STIX 2.1 bundle</span>
          <button onClick={copy} style={btn}>{copied ? "Copied ✓" : "Copy JSON"}</button>
          <button onClick={download} style={btn}>Download .json</button>
          <button onClick={() => handOffBundle(result, entity.name)} style={{ ...HP.primaryBtn, padding: "7px 13px", fontSize: fs(12) }} title="See exactly this selection as an interactive graph">Open in Graph</button>
          {onOpenBundle && <button onClick={() => onOpenBundle(result)} style={btn} title="Load it into your Workspace">Open in Viewer</button>}
        </div>
        <pre style={{ margin: 0, padding: 16, fontSize: fs(11), color: B.textMuted, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 600, overflowY: "auto" }}>
          {json.length > PREVIEW_MAX ? `${json.slice(0, PREVIEW_MAX)}\n\n… preview cut at ${nf(PREVIEW_MAX)} characters; Copy and Download include everything.` : json}
        </pre>
      </div>
    </div>
  );
}
